import type { Spread } from 'lexical';
import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  ParagraphNode,
  SerializedParagraphNode,
  SerializedTextNode,
  TextNode,
} from 'lexical';

// Module reloads can run this setup again, so this key prevents applying the change twice.
const paragraphExportPatch = Symbol.for('@main/ui/paragraph-export');

preserveParagraphSerialization();

// With a bold first child, 0.50 exports paragraph `{ ..., textFormat: 1, textStyle: '' }`.
// Use the paragraph's own format and omit textStyle to keep the 0.16.1 `{ ..., textFormat: 0 }` shape.
export function preserveParagraphSerialization() {
  const prototype = ParagraphNode.prototype;
  if (Reflect.get(prototype, paragraphExportPatch) === true) return;

  const exportJSON = prototype.exportJSON;
  prototype.exportJSON = function compatibleParagraphExport(): SerializedParagraphNode {
    const json = exportJSON.call(this) as SerializedParagraphNode & { textStyle?: string };
    json.textFormat = this.getTextFormat();
    delete (json as Partial<SerializedParagraphNode>).textStyle;
    return json;
  };
  Object.defineProperty(prototype, paragraphExportPatch, { value: true });
}

export interface MentionedObject {
  id: string;
  name: string;
  label?: string;
}

export type SerializedMentionNode = Spread<
  {
    obj: MentionedObject;
    type: 'mention';
    version: 1;
  },
  SerializedTextNode
>;

function convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent;

  if (textContent !== null) {
    const node = $createMentionNode({
      id: textContent,
      name: textContent,
    });

    return {
      node,
    };
  }

  return null;
}

const mentionStyle = 'background-color: rgba(24, 119, 232, 0.2)';
export class MentionNode extends TextNode {
  __obj: MentionedObject;

  static override getType(): string {
    return 'mention';
  }

  static override clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__obj, node.__key);
  }

  static override importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.obj);
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(obj: MentionedObject, key?: NodeKey) {
    super(obj.name, key);

    this.__obj = obj;
  }

  override exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      obj: this.__obj,
      type: 'mention',
      version: 1,
    };
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = mentionStyle;
    dom.className = 'mention';
    return dom;
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    element.textContent = this.__text;
    return { element };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  override isTextEntity(): true {
    return true;
  }

  getMentionObj(): MentionedObject {
    return this.__obj;
  }
}

export function $createMentionNode(obj: MentionedObject): MentionNode {
  const mentionNode = new MentionNode(obj);
  mentionNode.setMode('segmented').toggleDirectionless();
  return mentionNode;
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
